import { useParams } from 'react-router-dom';
import css from '../knowledge-base.module.scss';
import PageHeader from 'src/components/page-header/page-header';
import { KNOWLEDGE_BASE } from 'src/constants/routes';
import { FileView, Material } from 'src/types';
import { useCallback, useEffect, useState } from 'react';
import { useGetMaterial, useGetModule } from '../data/queries';
import Description from 'src/components/description-block/description';
import { ReactComponent as ArrowLeft } from 'src/assets/arrow-long-left.svg';
import { ReactComponent as ArrowRight } from 'src/assets/arrow-long-right.svg';
import MaterialsListItem from './materials-list-item';
import QuizInfoModal from 'src/pages/job-search/quiz/quiz-info-modal';
import { QuizEntityType } from 'src/types/quiz';
import { ReactComponent as DownloadIcon } from 'src/assets/download-icon.svg';
import ComplaintPopup from './complaint-popup';
import { useMetaContext } from 'src/hooks/meta-context';
import generalBannerSoldier from 'src/assets/general/general-banner-soldier.webp';

function Module() {
  const { slug, module } = useParams();
  const { updateMetaInfo } = useMetaContext();
  const [article, setArticle] = useState<string>('');
  const { data: currentModule } = useGetModule({
    module: module ?? '',
    parent: slug ?? ''
  });
  const [showModal, setShowModal] = useState(false);

  const { data: material } = useGetMaterial(article);
  const page = [
    {
      path: KNOWLEDGE_BASE.path,
      title: KNOWLEDGE_BASE.title
    },
    {
      path: `${KNOWLEDGE_BASE.path}/${slug}`,
      title: currentModule?.parentName ?? ''
    }
  ];

  const handleUrlChange = useCallback(
    (index = 0) => {
      const url = currentModule?.materials[index]?.url;
      setArticle(url?.replace('/articles/', ''));
    },
    [currentModule?.materials]
  );

  const index = currentModule?.materials?.indexOf(
    currentModule?.materials?.find((i: Material) => i.url.includes(article))
  );

  const prev = () => index !== 0 && handleUrlChange(index - 1);
  const next = () => index !== currentModule?.materials?.length - 1 && handleUrlChange(index + 1);

  currentModule &&
    updateMetaInfo({
      title: KNOWLEDGE_BASE.meta.title,
      description: KNOWLEDGE_BASE.meta.description,
      openGraph: {
        title: currentModule.title,
        description: currentModule.description.replace(/<\/?[^>]+(>|$)/g, ''),
        img: currentModule.imgUrl ?? generalBannerSoldier,
        type: 'website'
      }
    });

  useEffect(() => {
    handleUrlChange(0);
  }, [currentModule, handleUrlChange]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [article]);

  return (
    <main className={css.knowledge}>
      <PageHeader
        title={currentModule?.title ?? ''}
        description={currentModule?.description ?? ''}
        pages={page}
        filter={
          currentModule?.materials[index] && (
            <ComplaintPopup
              entityId={currentModule.materials[index].id}
              targetTitle={currentModule.materials[index].title}
            />
          )
        }
      />
      <div className={css.body}>
        <div className={css.module}>
          {!currentModule && <h1 className={css.emptyPlaceholder}>Стаття не знайдена</h1>}
          {currentModule && (
            <div className={css.content}>
              <details>
                <summary>Зміст</summary>
                <section className={css.materialsList}>
                  <ul>
                    {currentModule?.materials?.map((m: Material, index: number) => (
                      <MaterialsListItem
                        key={m.slug}
                        title={m.title}
                        onClick={() => handleUrlChange(index)}
                        isActive={article?.length > 0 && m.url.includes(article)}
                      />
                    ))}
                  </ul>
                </section>
              </details>
              {currentModule?.files?.length > 0 && (
                <details>
                  <summary>Додаткові матеріали</summary>
                  <section className={css.filesList}>
                    {currentModule.files.map((f: FileView) => (
                      <a key={f.url} className={css.listItem} href={f.url} target='_blank' rel='noopener noreferrer'>
                        {f.name}
                        <DownloadIcon />
                      </a>
                    ))}
                  </section>
                </details>
              )}
              <article>
                <h1 className={css.title}>{material?.title}</h1>
                <Description description={material?.description ?? ''} className={css.description} />
              </article>
              {showModal && (
                <QuizInfoModal
                  quizId={currentModule?.materials[index].quizId}
                  entityId={currentModule?.materials[index].id}
                  entityType={QuizEntityType.KnowledgeBase}
                  onClose={() => setShowModal(false)}
                />
              )}
              <div className={css.buttons}>
                <button className={css.action_btn} disabled={index === 0} onClick={prev}>
                  <ArrowLeft />
                  Попередній розділ
                </button>
                {currentModule?.materials[index]?.quizId && (
                  <button className={css.btn} onClick={() => setShowModal(true)}>
                    Перевірити свої знання
                  </button>
                )}
                <button
                  className={css.action_btn}
                  disabled={index === currentModule?.materials?.length - 1}
                  onClick={next}>
                  Наступний розділ
                  <ArrowRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Module;
